import { pubsub, cart } from '@services';
import { notyf } from '@modules';

class ProductForm extends HTMLElement {
  #unsubscribers = [];
  #form = this.querySelector('form[id^="product-form-"]');
  #quantityInput = this.#form?.querySelector('input[name="quantity"]');
  // Get all submit buttons that are part of the form
  #submitButtons = document.querySelectorAll(`button[form="${this.#form?.getAttribute('id')}"]`);

  connectedCallback() {
    this.#form?.addEventListener('submit', (event) => this.#onFormSubmit(event));

    this.#unsubscribers.push(
      pubsub.subscribe('variant:change', ({ id, data: { html } }) => {
        if (id !== this.dataset.productId) return;
        this.#onVariantChange(html);
      }),
      pubsub.subscribe('quantity:change', ({ quantity }) => {
        this.#quantityInput.value = quantity;
      })
    );
  }

  disconnectedCallback() {
    this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
  }

  /**
   * Replace section content on variant change
   * @param {Document} html - The HTML document
   */
  #onVariantChange(html) {
    const section = this.closest('[id^="shopify-section-"]');

    const sectionToReplace = html.getElementById(section.id);
    if (!sectionToReplace) return;

    const newSection = sectionToReplace.cloneNode(true);
    this.#restoreLineItemProperties(newSection);
    section.replaceWith(newSection);
  }

  /**
   * Carry line item property input (e.g. gift card recipient) over to the re-rendered section
   * @param {HTMLElement} newSection - The section that replaces the current one
   */
  #restoreLineItemProperties(newSection) {
    if (!this.#form) return;

    [...this.#form.elements]
      ?.filter(({ name }) => name?.startsWith('properties['))
      ?.forEach((field) => {
        const newField = newSection.querySelector(`[name="${CSS.escape(field.name)}"]`);
        if (!newField) return;

        if (field.type === 'checkbox') {
          newField.checked = field.checked;
        } else {
          newField.value = field.value;
        }
      });
  }

  /**
   * Handle form submission
   * @param {Event} event
   */
  async #onFormSubmit(event) {
    event.preventDefault();

    if (!this.#form.reportValidity()) return;

    this.#toggleLoading(true);

    try {
      await cart.add({ form: this.#form });
    } catch (error) {
      this.#onError(error);
    } finally {
      this.#toggleLoading(false);
    }
  }

  /**
   * Toggle loading state on submit buttons
   * @param {boolean} loading
   */
  #toggleLoading(loading) {
    this.#submitButtons?.forEach((button) => button.classList.toggle('loading', loading));
  }

  /**
   * Handle form error
   * @param {Error} error - The error object
   */
  #onError({ message }) {
    notyf.error(message);
  }
}

customElements.define('product-form', ProductForm);
